import type { FC, MouseEvent, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BaseDialogProps {
    open: boolean
    onClose: () => void
    labelledBy?: string
    label?: string
    className?: string
    children: ReactNode
}

/* Native <dialog>: focus trap, Escape to close and it renders in the top layer */
const useNativeDialog = (open: boolean, onClose: () => void) => {
    const ref = useRef<HTMLDialogElement>(null)
    const closeRef = useRef(onClose)

    closeRef.current = onClose

    useEffect(() => {
        const dialog = ref.current

        if (!dialog) return

        if (open && !dialog.open) {
            dialog.showModal()
            document.documentElement.style.overflow = 'hidden'
        }

        if (!open && dialog.open) dialog.close()

        if (!open) document.documentElement.style.overflow = ''
    }, [open])

    useEffect(() => {
        const dialog = ref.current

        if (!dialog) return

        const handleClose = () => {
            document.documentElement.style.overflow = ''
            closeRef.current()
        }

        dialog.addEventListener('close', handleClose)

        return () => {
            dialog.removeEventListener('close', handleClose)
            document.documentElement.style.overflow = ''
        }
    }, [])

    const onBackdropClick = (event: MouseEvent<HTMLElement>) => {
        if (event.target === event.currentTarget) closeRef.current()
    }

    return { ref, onBackdropClick }
}

export const Drawer: FC<BaseDialogProps> = ({ open, onClose, labelledBy, label, className, children }): ReactNode => {
    const { ref, onBackdropClick } = useNativeDialog(open, onClose)

    return (
        <dialog
            ref={ref}
            className='gj-dialog fixed inset-0 h-dvh w-screen'
            aria-labelledby={labelledBy}
            aria-label={label}
            onClick={onBackdropClick}
        >
            <div className={cn('gj-drawer-panel ml-auto flex h-full w-full max-w-md flex-col bg-background shadow-2xl', className)}>
                {open && children}
            </div>
        </dialog>
    )
}

interface ModalProps extends BaseDialogProps {
    title?: ReactNode
    description?: ReactNode
    size?: 'sm' | 'md' | 'lg'
    hideClose?: boolean
}

const MODAL_SIZES = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-3xl'
}

export const Modal: FC<ModalProps> = ({
    open,
    onClose,
    title,
    description,
    size = 'md',
    hideClose = false,
    labelledBy,
    label,
    className,
    children
}): ReactNode => {
    const { ref, onBackdropClick } = useNativeDialog(open, onClose)

    return (
        <dialog
            ref={ref}
            className='gj-dialog fixed inset-0 h-dvh w-screen'
            aria-labelledby={labelledBy}
            aria-label={label}
            onClick={onBackdropClick}
        >
            <div className='flex min-h-full items-center justify-center p-4' onClick={onBackdropClick}>
                <div className={cn('gj-modal-panel relative w-full rounded-[28px] border-2 border-brand-choco bg-background p-6 shadow-[0_10px_0_0_#3B1F0E] sm:p-7', MODAL_SIZES[size], className)}>
                    {!hideClose && (
                        <button
                            type='button'
                            onClick={onClose}
                            className='absolute right-4 top-4 inline-flex size-9 items-center justify-center rounded-full text-brand-choco transition-colors hover:bg-brand-sand focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40'
                            aria-label='Tutup'
                        >
                            <X className='size-5' />
                        </button>
                    )}

                    {title && (
                        <h2 id={labelledBy} className='pr-10 font-heading text-2xl font-bold text-brand-choco'>
                            {title}
                        </h2>
                    )}

                    {description && (
                        <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                            {description}
                        </p>
                    )}

                    {open && (
                        <div className={cn(title || description ? 'mt-5' : '')}>
                            {children}
                        </div>
                    )}
                </div>
            </div>
        </dialog>
    )
}
